import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminHeader } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ArrowRight, ExternalLink, Trash2, AlertTriangle } from "lucide-react";

const ALL_MODULES = ["dashboard", "customers", "sites", "contracts", "assets", "jobs", "schedule", "defects", "quotations", "stock", "purchasing", "projects", "people", "vehicles", "billing", "documents", "reports", "settings"];

export default function Companies() {
  const nav = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ accent_color: "#2563EB", currency: "EUR", plan: "Professional", status: "active", modules: ["dashboard", "customers", "sites", "jobs", "schedule", "billing", "reports", "settings"] });
  const [delCompany, setDelCompany] = useState(null);
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get("/admin/companies").then((r) => setCompanies(r.data));
  useEffect(() => { load(); api.get("/admin/templates").then((r) => setTemplates(r.data)); }, []);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (t) setForm((f) => ({ ...f, industry_template: id, modules: t.modules, terminology: t.terminology }));
  };
  const toggle = (m) => setForm((f) => ({ ...f, modules: f.modules.includes(m) ? f.modules.filter((x) => x !== m) : [...f.modules, m] }));

  const create = async () => {
    if (!form.name) return toast.error("Company name required");
    try { await api.post("/admin/companies", form); toast.success("Company created"); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openWorkspace = (cid) => { localStorage.setItem("active_company_id", cid); nav("/app/dashboard"); };

  const deleteForever = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/companies/${delCompany.id}`, { data: { confirm_name: delConfirm } });
      toast.success(`${delCompany.name} deleted forever — ${data.records_removed} records removed`);
      setDelCompany(null); setDelConfirm(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setDeleting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminHeader />
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div><h1 className="text-2xl font-head font-bold text-slate-900">Client Companies</h1><p className="text-sm text-slate-500">Create and manage Amden client tenants</p></div>
          <Button className="btn-accent" data-testid="new-company-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" />New Company</Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-md p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-9 w-9 rounded-md grid place-items-center text-white font-head font-bold" style={{ backgroundColor: c.accent_color }}>{c.name[0]}</div>
                <div><div className="font-head font-bold text-slate-900">{c.name}</div><div className="text-xs text-slate-400">{c.trading_name}</div></div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 mb-3">
                <div>Plan: <span className="text-slate-700">{c.plan}</span></div>
                <div>Status: <span className="text-slate-700 capitalize">{c.status}</span></div>
                <div>Currency: <span className="text-slate-700">{c.currency}</span></div>
                <div>Modules: <span className="text-slate-700">{(c.modules || []).length}</span></div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" data-testid={`company-config-${c.id}`} onClick={() => nav(`/admin/companies/${c.id}`)}>Configure</Button>
                <Button size="sm" className="btn-accent flex-1" data-testid={`company-open-${c.id}`} onClick={() => openWorkspace(c.id)}>Open <ExternalLink className="h-3.5 w-3.5 ml-1" /></Button>
                <Button size="sm" variant="outline" data-testid={`company-delete-${c.id}`} title="Delete company forever"
                  className="px-2 text-slate-400 hover:text-red-600 hover:border-red-300"
                  onClick={() => { setDelCompany(c); setDelConfirm(""); }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">New Client Company</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-600">Industry template</Label>
              <Select onValueChange={applyTemplate}><SelectTrigger data-testid="company-template" className="mt-1 bg-white"><SelectValue placeholder="Start from template…" /></SelectTrigger>
                <SelectContent className="bg-white">{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs text-slate-600">Plan</Label>
              <Select value={form.plan} onValueChange={(v) => setForm((f) => ({ ...f, plan: v }))}><SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{["Starter", "Professional", "Enterprise"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
            {[["name", "Company name"], ["trading_name", "Trading name"], ["contact_email", "Contact email"], ["contact_phone", "Contact phone"]].map(([k, l]) => (
              <div key={k}><Label className="text-xs text-slate-600">{l}</Label><Input data-testid={`company-${k}`} value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="mt-1 bg-white" /></div>
            ))}
            <div><Label className="text-xs text-slate-600">Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}><SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{["EUR", "GBP", "USD"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs text-slate-600">Accent colour</Label>
              <input type="color" data-testid="company-accent" value={form.accent_color} onChange={(e) => setForm((f) => ({ ...f, accent_color: e.target.value }))} className="mt-1 h-9 w-full rounded border border-slate-200" /></div>
          </div>
          <div><Label className="text-xs text-slate-600 mb-1 block">Enabled modules</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {ALL_MODULES.map((m) => <label key={m} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-2 py-1.5"><span className="text-xs capitalize">{m}</span><Switch data-testid={`company-module-${m}`} checked={form.modules.includes(m)} onCheckedChange={() => toggle(m)} disabled={m === "dashboard" || m === "settings"} /></label>)}
            </div></div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="btn-accent" data-testid="company-create-save" onClick={create}>Create company</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delCompany} onOpenChange={(o) => { if (!o) { setDelCompany(null); setDelConfirm(""); } }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />Delete {delCompany?.name} forever?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              This permanently deletes <span className="font-semibold">{delCompany?.name}</span> and <span className="font-semibold text-red-700">ALL of its data</span> — users, customers, sites, jobs, contracts, stock, recurring works, files, everything. This cannot be undone. A platform audit record will be kept.
            </p>
            <div>
              <Label className="text-xs text-slate-600">Type the company name to confirm</Label>
              <Input data-testid="company-delete-confirm-input" value={delConfirm} autoFocus
                placeholder={delCompany?.name} onChange={(e) => setDelConfirm(e.target.value)} className="mt-1 bg-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="company-delete-cancel" onClick={() => { setDelCompany(null); setDelConfirm(""); }}>Cancel</Button>
            <Button data-testid="company-delete-confirm-btn" className="bg-red-600 hover:bg-red-700 text-white"
              disabled={deleting || delConfirm.trim() !== (delCompany?.name || "")} onClick={deleteForever}>
              {deleting ? "Deleting…" : "Delete forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
